/* VoiceAnalyticsPanel — admin view of what cooks are actually SAYING
 * to the voice overlay. Surfaces the top 20 phrases ordered by count,
 * split into matched (we handled it) and miss (no regex / entity hit).
 * Phrases with low match ratios are the ones to prioritise when tuning
 * the regex table in VoiceSearchContext.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChartLine, Microphone, WarningCircle, CheckCircle, Sparkle, XCircle } from "@phosphor-icons/react";
import api from "@/lib/api";

export default function VoiceAnalyticsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [reviewBusy, setReviewBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/admin/voice/top-phrases?limit=20"),
        api.get("/admin/voice/auto-tune/suggestions?status=pending"),
      ]);
      setRows(r1.data?.phrases || []);
      setSuggestions(r2.data?.suggestions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load phrase analytics");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runAutoTune = async () => {
    if (!window.confirm("Scan the voice-phrase log for frequent misses and draft regex suggestions via Claude? Each phrase scanned uses a small amount of Emergent LLM credit.")) return;
    setScanning(true);
    try {
      const { data } = await api.post("/admin/voice/auto-tune/scan");
      toast.success(`Scanned ${data.scanned} phrase${data.scanned === 1 ? "" : "s"} — ${data.created} suggestion${data.created === 1 ? "" : "s"} drafted.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-tune scan failed");
    } finally { setScanning(false); }
  };

  const review = async (id, action) => {
    setReviewBusy(id);
    try {
      await api.post(`/admin/voice/auto-tune/suggestions/${id}/${action}`);
      toast.success(action === "approve" ? "Approved — pattern is live site-wide." : "Dismissed.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Review failed");
    } finally { setReviewBusy(""); }
  };

  const total = rows.reduce((a, r) => a + (r.count || 0), 0);
  const matched = rows.reduce((a, r) => a + (r.matched_count || 0), 0);
  const overallRatio = total ? Math.round((matched / total) * 100) : 0;

  return (
    <section data-testid="admin-voice-analytics">
      <p className="overline mb-2">Voice telemetry</p>
      <h2 className="font-display text-3xl font-black text-rf-ink mb-1">Top voice phrases</h2>
      <p className="text-rf-ink-soft mb-6 text-sm max-w-3xl">
        What cooks are actually saying to the voice overlay. Phrases with a <b>low match ratio</b> are the
        ones to prioritise when tuning the regex / wake-word table — a cook tried, we didn&apos;t resolve.
      </p>

      <div className="grid sm:grid-cols-3 gap-3 mb-5" data-testid="voice-analytics-summary">
        <SummaryTile icon={<ChartLine size={18} weight="duotone" />} label="Phrases tracked" value={rows.length} tone="ink" />
        <SummaryTile icon={<Microphone size={18} weight="duotone" />} label="Total utterances" value={total} tone="ink" />
        <SummaryTile
          icon={overallRatio >= 70 ? <CheckCircle size={18} weight="duotone" /> : <WarningCircle size={18} weight="duotone" />}
          label="Match ratio"
          value={`${overallRatio}%`}
          tone={overallRatio >= 70 ? "ok" : "warn"}
        />
      </div>

      {/* ---------- Auto-tune: AI-drafted regex suggestions ---------- */}
      <div className="card-recipe !p-5 mb-6 bg-rf-amber/10 border !border-rf-amber/40" data-testid="voice-auto-tune-card">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Sparkle size={20} weight="duotone" className="text-rf-orange" />
            <h3 className="font-display font-black text-rf-ink text-xl">AI auto-tune</h3>
          </div>
          <button
            type="button" onClick={runAutoTune} disabled={scanning}
            data-testid="voice-auto-tune-scan"
            className="btn-amber !py-2 !px-4 !text-sm disabled:opacity-50"
          >
            {scanning ? "Scanning…" : "Scan missed phrases now"}
          </button>
        </div>
        <p className="text-xs text-rf-ink-soft mb-4 max-w-2xl">
          When a phrase racks up <b>5+ misses</b> in the last week, Claude drafts a regex pattern and
          routing suggestion. Approve one to make it live site-wide instantly — the client-side
          dispatcher fetches approved patterns on every page load.
        </p>

        {suggestions.length === 0 && (
          <p className="text-sm text-rf-ink-soft italic" data-testid="voice-auto-tune-empty">
            No pending suggestions. Run a scan if you&apos;ve had enough voice traffic to accumulate misses.
          </p>
        )}

        {suggestions.length > 0 && (
          <ul className="space-y-3" data-testid="voice-auto-tune-list">
            {suggestions.map((s) => {
              const busy = reviewBusy === s.suggestion_id;
              return (
                <li key={s.suggestion_id} className="rounded-xl bg-white p-4 border border-rf-amber/30" data-testid={`voice-auto-tune-${s.suggestion_id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-display font-black text-rf-ink text-base truncate">&ldquo;{s.phrase}&rdquo;</p>
                      <p className="text-xs text-rf-ink-soft mt-1">
                        <b>{s.miss_count_at_scan}</b> misses at scan · target: <code>{s.target}</code>
                        {s.path ? <> · path: <code className="text-rf-orange-ink">{s.path}</code></> : null}
                      </p>
                    </div>
                    <span className="chip">{s.label || s.target}</span>
                  </div>
                  <div className="rounded-lg bg-rf-cream-alt p-2 mb-2">
                    <p className="text-[11px] uppercase tracking-wider font-bold text-rf-ink-soft mb-1">Draft regex</p>
                    <code className="text-xs text-rf-ink break-all">/{s.pattern}/i</code>
                  </div>
                  {s.reasoning && (
                    <p className="text-xs text-rf-ink-soft italic mb-3">Claude: {s.reasoning}</p>
                  )}
                  <div className="flex gap-2">
                    <button
                      type="button" disabled={busy}
                      onClick={() => review(s.suggestion_id, "approve")}
                      data-testid={`voice-approve-${s.suggestion_id}`}
                      className="btn-amber !py-1.5 !px-3 !text-sm inline-flex items-center gap-1 disabled:opacity-50"
                    >
                      <CheckCircle size={14} weight="bold" /> Approve &amp; go live
                    </button>
                    <button
                      type="button" disabled={busy}
                      onClick={() => review(s.suggestion_id, "dismiss")}
                      data-testid={`voice-dismiss-${s.suggestion_id}`}
                      className="chip !bg-rf-cream-alt text-xs inline-flex items-center gap-1 disabled:opacity-50"
                    >
                      <XCircle size={14} weight="bold" /> Dismiss
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {loading && <p className="text-rf-ink-soft italic">Loading…</p>}

      {!loading && rows.length === 0 && (
        <p className="text-rf-ink-soft italic" data-testid="admin-voice-analytics-empty">
          No voice phrases logged yet. The overlay will start logging the moment a user says anything.
        </p>
      )}

      {!loading && rows.length > 0 && (
        <ol className="space-y-2" data-testid="admin-voice-analytics-list">
          {rows.map((r, i) => {
            const pct = Math.round((r.match_ratio || 0) * 100);
            const warn = pct < 50;
            return (
              <li
                key={r.phrase}
                className={`card-recipe !p-4 flex items-center gap-4 ${warn ? "!bg-amber-50 border !border-amber-200" : ""}`}
                data-testid={`voice-phrase-row-${i}`}
              >
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-rf-orange/90 text-white font-bold text-sm flex items-center justify-center">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-display font-black text-rf-ink text-base truncate">&ldquo;{r.phrase}&rdquo;</p>
                  <p className="text-xs text-rf-ink-soft mt-0.5">
                    {r.count} total · <b>{r.matched_count}</b> matched · {r.miss_count} miss ·
                    last resolved by <code className="text-[11px]">{r.last_tier || "—"}</code>
                  </p>
                </div>
                <div className={`chip !text-xs ${warn ? "!bg-amber-500/25 !text-amber-900" : "!bg-emerald-500/15 !text-emerald-800"}`}>
                  {pct}% hit
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

function SummaryTile({ icon, label, value, tone }) {
  const toneCls = tone === "ok"
    ? "!bg-emerald-500/10 text-emerald-900 border !border-emerald-200"
    : tone === "warn"
    ? "!bg-amber-500/15 text-amber-900 border !border-amber-200"
    : "!bg-rf-cream-alt text-rf-ink";
  return (
    <div className={`card-recipe !p-4 flex items-center gap-3 ${toneCls}`}>
      <span className="text-rf-orange">{icon}</span>
      <div>
        <p className="text-[10px] uppercase tracking-wider font-bold opacity-70">{label}</p>
        <p className="font-display font-black text-2xl">{value}</p>
      </div>
    </div>
  );
}
